import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion, useIsPresent, useReducedMotion } from "motion/react";
import { FiSearch } from "react-icons/fi";
import { spring } from "../lib/springs";
import { useSurface, SurfaceProvider } from "../lib/surface-context";
import { surfaceClasses } from "../lib/surface-classes";

/* ============================================================================
   Command palette — jump to any page the sidebar offers. Adapted from
   beui.dev/components/blocks/command-palette.

     <CommandPalette items open onOpenChange />

   item: { id, label, group?, parent?, keywords?, icon?, badge?, onSelect }
     group    section heading the row is listed under
     parent   the sidebar group it lives in ("Leave"), shown muted on the right
              and searchable, because "Listing" or "Add Tracker" alone is
              ambiguous

   Controlled: the shell owns `open` so the trigger at the top of the sidebar
   and Cmd/Ctrl+K drive the same state. Focus stays in the search field the
   whole time (combobox + aria-activedescendant); Up/Down move the cursor,
   Enter runs it, Escape or a backdrop click closes, and focus goes back to
   whatever had it before.
   ========================================================================== */

// A dialog-sized surface: it enters on the slow tier (0.24s) and leaves on
// that tier's quicker exit tween. The highlight is a hover, so it travels on
// the fast tier.
const PANEL = spring.slow;
const EXIT = spring.slow.exit;

const normalise = (s) => (s ?? "").toString().toLowerCase();

/* Rank, don't just filter: every query token has to appear somewhere in the
   item, and the label decides the order — whole-label prefix, then a word in
   the label starting with the query, then the label containing it, then a
   match only in parent/group/keywords. A query that matches nothing that way
   falls back to an in-order subsequence of the label ("mngl" → Manage Leave). */
export const searchCommands = (items, query) => {
  const q = normalise(query).trim();
  if (!q) return items;
  const tokens = q.split(/\s+/);

  const scored = [];
  items.forEach((item, index) => {
    const label = normalise(item.label);
    const haystack = [label, item.parent, item.group, ...(item.keywords ?? [])]
      .map(normalise)
      .join(" ");

    let score = null;
    if (tokens.every((t) => haystack.includes(t))) {
      if (label.startsWith(q)) score = 0;
      else if (label.split(/\s+/).some((w) => w.startsWith(q))) score = 1;
      else if (label.includes(q)) score = 2;
      else score = 3;
    } else {
      const compact = q.replace(/\s+/g, "");
      let at = 0;
      for (const ch of label) if (ch === compact[at]) at += 1;
      if (at === compact.length) score = 4;
    }
    if (score !== null) scored.push({ item, score, index });
  });

  return scored
    .sort((a, b) => a.score - b.score || a.index - b.index)
    .map((s) => s.item);
};

// An exiting layer must stop taking clicks the moment it starts leaving,
// not when its animation ends — `open` is already false for those frames.
const Presence = ({ children }) => children(useIsPresent());

const isMac =
  typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.userAgent);
export const SHORTCUT_LABEL = isMac ? "⌘K" : "Ctrl K";

const CommandPalette = ({
  items,
  open,
  onOpenChange,
  shortcut = "k",
  placeholder = "Search pages…",
  emptyMessage = "No pages found.",
}) => {
  const [query, setQuery] = useState("");
  const [cursor, setCursor] = useState(0);
  const uid = useId();
  const reduce = useReducedMotion();
  // four surface steps above the page, like every dialog
  const level = Math.min(useSurface() + 4, 8);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const returnFocusRef = useRef(null);
  const onOpenChangeRef = useRef(onOpenChange);
  onOpenChangeRef.current = onOpenChange;

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === shortcut) {
        // CKEditor binds Ctrl/Cmd+K to "insert link" — leave rich text alone.
        if (e.defaultPrevented || e.target?.isContentEditable) return;
        e.preventDefault();
        onOpenChangeRef.current(!open);
        return;
      }
      if (e.key === "Escape" && open) {
        e.preventDefault();
        onOpenChangeRef.current(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, shortcut]);

  // Layout effect, so a reopened palette never paints last time's query.
  useLayoutEffect(() => {
    if (!open) return;
    returnFocusRef.current = document.activeElement;
    setQuery("");
    setCursor(0);

    const root = document.documentElement;
    const prevRoot = root.style.overflow;
    const prevBody = document.body.style.overflow;
    root.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
    const frame = requestAnimationFrame(() => inputRef.current?.focus());

    return () => {
      cancelAnimationFrame(frame);
      root.style.overflow = prevRoot;
      document.body.style.overflow = prevBody;
      const back = returnFocusRef.current;
      // a route change may have unmounted it
      if (back && back.isConnected && typeof back.focus === "function") back.focus();
    };
  }, [open]);

  const filtered = useMemo(() => searchCommands(items, query), [items, query]);
  const hasIcons = useMemo(() => items.some((it) => it.icon), [items]);

  const grouped = useMemo(() => {
    const map = new Map();
    filtered.forEach((it) => {
      const g = it.group ?? "Results";
      if (!map.has(g)) map.set(g, []);
      map.get(g).push(it);
    });
    return Array.from(map.entries());
  }, [filtered]);

  // Grouping reorders the ranked list, so the highlight, the ids, Enter and
  // the scroll all read this one render-order array and cannot drift apart.
  const rows = useMemo(() => grouped.flatMap(([, list]) => list), [grouped]);
  const active = rows.length ? Math.min(cursor, rows.length - 1) : -1;

  const run = (item) => {
    onOpenChange(false);
    item.onSelect();
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      if (!rows.length) return;
      const step = e.key === "ArrowDown" ? 1 : -1;
      setCursor((active + step + rows.length) % rows.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (rows[active]) run(rows[active]);
    } else if (e.key === "Tab") {
      // the field is the only stop; the rows are reached with the arrows
      e.preventDefault();
    }
  };

  useEffect(() => {
    if (!open) return;
    listRef.current
      ?.querySelector(`[data-index="${active}"]`)
      ?.scrollIntoView({ block: "nearest" });
  }, [active, open]);

  return createPortal(
    <AnimatePresence initial={false}>
      {open ? (
        <Presence key="backdrop">
          {(isPresent) => (
            <motion.button
              type="button"
              tabIndex={-1}
              aria-label="Close command palette"
              className="cp-backdrop"
              style={{ pointerEvents: isPresent ? "auto" : "none" }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0, transition: EXIT }}
              transition={PANEL}
              onClick={() => onOpenChange(false)}
            />
          )}
        </Presence>
      ) : null}

      {open ? (
        <Presence key="panel">
          {(isPresent) => (
            <div className="cp-layer" inert={isPresent ? undefined : ""}>
              <motion.div
                role="dialog"
                aria-modal="true"
                aria-label="Command palette"
                className={"cp-panel " + surfaceClasses(level)}
                initial={{ opacity: 0, y: reduce ? 0 : -8, scale: reduce ? 1 : 0.97 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{
                  opacity: 0,
                  y: reduce ? 0 : -8,
                  scale: reduce ? 1 : 0.97,
                  transition: EXIT,
                }}
                transition={reduce ? spring.fast : PANEL}
                onKeyDown={onKeyDown}
              >
                <SurfaceProvider value={level}>
                <div className="cp-search">
                  <FiSearch className="cp-search-icon" aria-hidden="true" />
                  <input
                    ref={inputRef}
                    className="cp-input"
                    value={query}
                    onChange={(e) => {
                      setQuery(e.target.value);
                      setCursor(0);
                    }}
                    placeholder={placeholder}
                    role="combobox"
                    aria-expanded="true"
                    aria-controls={`${uid}-list`}
                    aria-activedescendant={active >= 0 ? `${uid}-opt-${active}` : undefined}
                    aria-autocomplete="list"
                    autoComplete="off"
                    spellCheck={false}
                  />
                  <kbd className="cp-kbd">ESC</kbd>
                </div>

                <motion.div
                  ref={listRef}
                  id={`${uid}-list`}
                  role="listbox"
                  aria-label="Pages"
                  className="cp-list"
                  layoutScroll
                >
                  {rows.length === 0 ? (
                    <div className="cp-empty">{emptyMessage}</div>
                  ) : (
                    grouped.map(([group, list]) => (
                      <div key={group} className="cp-group">
                        <div className="cp-group-label" aria-hidden="true">
                          {group}
                        </div>
                        {list.map((it) => {
                          const idx = rows.indexOf(it);
                          const isActive = idx === active;
                          return (
                            <button
                              key={it.id}
                              type="button"
                              tabIndex={-1}
                              id={`${uid}-opt-${idx}`}
                              role="option"
                              aria-selected={isActive}
                              data-index={idx}
                              className={"cp-item" + (isActive ? " is-active" : "")}
                              // mousemove, not mouseenter: keyboard scrolling
                              // slides rows under a resting pointer
                              onMouseMove={() => {
                                if (!isActive) setCursor(idx);
                              }}
                              onClick={() => run(it)}
                            >
                              {isActive ? (
                                <motion.span
                                  layoutId={`${uid}-active`}
                                  className="cp-item-pill"
                                  transition={reduce ? { duration: 0 } : spring.fast}
                                  aria-hidden="true"
                                />
                              ) : null}
                              {it.icon || hasIcons ? (
                                <span className="cp-item-icon" aria-hidden="true">
                                  {it.icon}
                                </span>
                              ) : null}
                              <span className="cp-item-label">{it.label}</span>
                              {it.badge ? <span className="cp-item-badge">{it.badge}</span> : null}
                              {it.parent && it.parent !== it.label ? (
                                <span className="cp-item-parent">{it.parent}</span>
                              ) : null}
                            </button>
                          );
                        })}
                      </div>
                    ))
                  )}
                </motion.div>

                <div className="cp-foot" aria-hidden="true">
                  <span>
                    <kbd className="cp-kbd">↑</kbd>
                    <kbd className="cp-kbd">↓</kbd> to move
                  </span>
                  <span>
                    <kbd className="cp-kbd">↵</kbd> to open
                  </span>
                </div>
                </SurfaceProvider>
              </motion.div>
            </div>
          )}
        </Presence>
      ) : null}
    </AnimatePresence>,
    document.body
  );
};

export default CommandPalette;
